import { useSessionStorage } from 'usehooks-ts';

import { getKstToday } from '@/utils/kstDate';

/**
 * 캠페인 랜딩(/l/[slug])으로 들어온 방문자의 캠페인 정보.
 * 같은 탭에서는 다른 페이지로 가도 캠페인 상태(가입 화면 문구, 로고 이동, GA funnel)를 유지하는 데 쓴다
 */
export type SessionLandingCampaign = {
  slug: string;
  /** 캠페인 페이지에서 referral 에 같이 저장한 추천인 코드. 가입 화면은 이 코드가 아직 referral 에 남아 있을 때만 캠페인 문구를 쓴다 */
  referralCode: string;
  /** YYYY-MM-DD(한국 날짜). 지나면 저장돼 있어도 없는 것으로 본다 */
  endAt: string;
  signup: { title: string; highlight: string };
};

export const LANDING_CAMPAIGN_STORAGE_KEY = 'landing_campaign';

/**
 * 저장된 값이 지금도 유효한 캠페인인지.
 * 모양이 다르거나(예전 버전이 저장한 값) 종료일이 지났으면 false
 */
export const isActiveLandingCampaign = (
  value: unknown,
  today: string = getKstToday(),
): value is SessionLandingCampaign => {
  if (!value || typeof value !== 'object') return false;
  const { slug, referralCode, endAt, signup } = value as Partial<SessionLandingCampaign>;
  return (
    typeof slug === 'string' &&
    slug !== '' &&
    typeof referralCode === 'string' &&
    typeof endAt === 'string' &&
    endAt >= today &&
    typeof signup?.title === 'string' &&
    typeof signup?.highlight === 'string'
  );
};

/**
 * 서버에서 미리 그리는 영역(예: 공통 헤더, 가입 화면)에서는 `{ initializeWithValue: false }` 를 넘긴다.
 * 첫 렌더를 서버와 같은 null 로 맞춰야 hydration 불일치가 안 난다.
 * 종료일이 지났거나 모양이 다른 값은 null 로 돌려준다
 */
export const useSessionLandingCampaign = (options?: { initializeWithValue: false }) => {
  const [stored, setLandingCampaign, removeLandingCampaign] =
    useSessionStorage<SessionLandingCampaign | null>(LANDING_CAMPAIGN_STORAGE_KEY, null, options);
  const landingCampaign = isActiveLandingCampaign(stored) ? stored : null;

  return [landingCampaign, setLandingCampaign, removeLandingCampaign] as const;
};

/** 로고처럼 처음 화면으로 보내는 링크의 주소. 캠페인 랜딩을 거쳐 온 탭이면 메인 대신 그 캠페인 페이지로 보낸다 */
export const useLandingHomeHref = () => {
  const [landingCampaign] = useSessionLandingCampaign({ initializeWithValue: false });
  return landingCampaign ? `/l/${landingCampaign.slug}/` : '/';
};
